import { useMutation } from '@tanstack/react-query';
import { EventNamesMap, useAmplitude } from 'src/context/AmplitudeProvider';
import { useAppUI } from 'src/context/AppUIProvider/useAppUI';
import useAuthContext from 'src/context/AuthProvider/useAuthContext';
import { queryClient } from 'src/context/QueryProvider/QueryClient';
import { VerifyCodeResponse } from 'src/features/verification/data/service/VerificationService.types';
import { VerificationError } from 'src/features/verification/domain/VerificationRepositoryInterface';
import SendOtpUseCase from 'src/features/verification/domain/useCases/SendOtpUseCase';
import VerifyOtpUseCase from 'src/features/verification/domain/useCases/VerifyOtpUseCase';
import {
  SendPhoneOtpParams,
  SendEmailOtpParams,
  VerifyPhoneOtpParams,
  VerifyEmailOtpParams,
} from 'src/features/verification/domain/useCases/types';
import { diContainer } from 'src/inversify.config';
import { VerificationChannels } from 'src/types/common';
import resolveVariablesInMessage from 'src/utils/errors/resolveVariablesInMessage';

const sendOtpUseCase = diContainer.get<SendOtpUseCase>(SendOtpUseCase);
const verifyOtpUseCase = diContainer.get<VerifyOtpUseCase>(VerifyOtpUseCase);

export default function useOtpVerification() {
  const { logEvent } = useAmplitude();
  const { user, session, setSession } = useAuthContext();
  const { alertNotification } = useAppUI();

  const sendPhoneVerificationCodeMutation = useMutation({
    mutationKey: ['sendPhoneVerificationCode'],
    mutationFn: ({
      to,
      channel = VerificationChannels.whatsapp,
    }: SendPhoneOtpParams) => {
      const payload: SendPhoneOtpParams = {
        to,
        channel,
        ...(session?.accessToken && { accessToken: session.accessToken }),
      };

      return sendOtpUseCase.execute(payload);
    },
    onSuccess: (_, params) => {
      const [prevPhoneNumer, newPhoneNumber] = [
        user?.phone_number.slice(),
        params.to,
      ];
      const modifiedPhone = newPhoneNumber !== prevPhoneNumer;

      logEvent(EventNamesMap.PhoneVerificationCodeSent, {
        phone: params.to,
        user_id: user?.pk as number,
        modified_phone: modifiedPhone,
      });
    },
    onError: ({ message }, params) => {
      const alternativeChannel =
        params.channel === VerificationChannels.whatsapp
          ? VerificationChannels.sms
          : VerificationChannels.whatsapp;
      const resolvedMessage = resolveVariablesInMessage(message, {
        alternative_channel: alternativeChannel,
      });

      alertNotification.error(resolvedMessage);
    },
  });

  const sendEmailVerificationCodeMutation = useMutation({
    mutationKey: ['sendEmailVerificationCode'],
    mutationFn: ({ to }: Pick<SendEmailOtpParams, 'to' | 'accessToken'>) => {
      const payload: SendEmailOtpParams = {
        to,
        channel: VerificationChannels.email,
        ...(session?.accessToken && { accessToken: session.accessToken }),
      };

      return sendOtpUseCase.execute(payload);
    },
    onSettled: (_data, error, params) => {
      if (error) {
        alertNotification.error(error.message);
        return;
      }

      logEvent(EventNamesMap.EmailVerificationCodeSent, {
        email: params.to,
        user_id: user?.pk as number,
      });
    },
  });

  const verifyPhoneCodeMutation = useMutation({
    mutationKey: ['verifyPhoneCode'],
    mutationFn: ({
      to,
      verificationCode,
      channel = VerificationChannels.whatsapp,
    }: VerifyPhoneOtpParams) => {
      return verifyOtpUseCase.execute({
        to,
        verificationCode,
        channel,
        ...(session?.accessToken && { accessToken: session.accessToken }),
      });
    },
    onSettled: (data, error, params) => {
      if (error) {
        return handlePhoneVerificationError(error as VerificationError, params);
      }

      if (data) {
        handlePhoneVerificationSuccess(data, params);
      }
    },
  });

  const handlePhoneVerificationError = (
    error: VerificationError,
    params: VerifyPhoneOtpParams
  ) => {
    const message = error.message;
    const modifiedPhone = user?.phone_number !== params.to;

    logEvent(EventNamesMap.PhoneVerificationError, {
      phone: params.to,
      user_id: user?.pk as number,
      modified_phone: modifiedPhone,
      error_message: message,
      error_code: error.code,
    });

    const alternativeChannel =
      params.channel === VerificationChannels.whatsapp
        ? VerificationChannels.sms
        : VerificationChannels.whatsapp;
    const resolvedMessage = resolveVariablesInMessage(message, {
      alternative_channel: alternativeChannel,
    });

    alertNotification.error(resolvedMessage);
  };

  const handlePhoneVerificationSuccess = (
    data: VerifyCodeResponse,
    params: VerifyPhoneOtpParams
  ) => {
    const [prevPhoneNumer, newPhoneNumber] = [
      user?.phone_number.slice(),
      params.to,
    ];
    const modifiedPhone = newPhoneNumber !== prevPhoneNumer;

    logEvent(EventNamesMap.PhoneVerification, {
      phone: params.to,
      user_id: user?.pk as number,
      modified_phone: modifiedPhone,
    });

    queryClient.invalidateQueries({ queryKey: ['profile'] });

    const isValid = data?.is_valid;

    isValid &&
      setSession?.(
        session
          ? {
              ...session,
              user: {
                ...session.user,
                phone_number: data.phone_number,
                phone_verified: true,
              },
            }
          : undefined
      );
  };

  const verifyEmailCodeMutation = useMutation({
    mutationKey: ['verifyEmailCode'],
    mutationFn: ({
      to,
      verificationCode,
    }: Omit<VerifyEmailOtpParams, 'channel'>) => {
      return verifyOtpUseCase.execute({
        to,
        verificationCode,
        channel: VerificationChannels.email,
        ...(session?.accessToken && { accessToken: session.accessToken }),
      });
    },
    onSuccess: (_, params) => {
      logEvent(EventNamesMap.EmailVerification, {
        email: params.to,
        user_id: user?.pk as number,
      });
    },
    onError: (error: VerificationError, params) => {
      const message = error.message;

      logEvent(EventNamesMap.EmailVerificationError, {
        email: params.to,
        user_id: user?.pk as number,
        error_message: message,
        error_code: error.code,
      });

      alertNotification.error(error.message);
    },
  });

  return {
    sendPhoneVerificationCodeMutation,
    sendEmailVerificationCodeMutation,
    verifyPhoneCodeMutation,
    verifyEmailCodeMutation,
  };
}
